class TOTPGenerator {
  private static readonly PERIOD = 30
  private static readonly DIGITS = 6
  private static readonly BASE32_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'

  private base32Decode(base32: string): Uint8Array {
    base32 = base32.toUpperCase().replace(/[^A-Z2-7]/g, '')

    if (base32.length === 0) {
      throw new Error('Empty base32 string')
    }

    let bits = ''
    for (const char of base32) {
      const index = TOTPGenerator.BASE32_CHARS.indexOf(char)
      if (index === -1) {
        throw new Error(`Invalid base32 character: ${char}`)
      }
      bits += index.toString(2).padStart(5, '0')
    }

    const bytes = []
    for (let i = 0; i < bits.length; i += 8) {
      const byte = bits.slice(i, i + 8)
      if (byte.length === 8) {
        bytes.push(parseInt(byte, 2))
      }
    }

    return new Uint8Array(bytes)
  }

  private async hmacSha1(
    key: Uint8Array,
    message: Uint8Array
  ): Promise<Uint8Array> {
    try {
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        key as BufferSource,
        { name: 'HMAC', hash: 'SHA-1' },
        false,
        ['sign']
      )

      const signature = await crypto.subtle.sign(
        'HMAC',
        cryptoKey,
        message as BufferSource
      )
      return new Uint8Array(signature)
    } catch (error) {
      throw new Error(
        `HMAC-SHA1 failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  private timestampToCounter(timestamp: number): Uint8Array {
    const counter = Math.floor(timestamp / 1000 / TOTPGenerator.PERIOD)
    const buffer = new ArrayBuffer(8)
    const view = new DataView(buffer)
    view.setUint32(4, counter, false)
    return new Uint8Array(buffer)
  }

  private dynamicTruncate(hash: Uint8Array): number {
    const offset = hash[hash.length - 1] & 0x0f

    const code =
      ((hash[offset] & 0x7f) << 24) |
      ((hash[offset + 1] & 0xff) << 16) |
      ((hash[offset + 2] & 0xff) << 8) |
      (hash[offset + 3] & 0xff)

    return code % Math.pow(10, TOTPGenerator.DIGITS)
  }

  async generateTOTP(secret: string, timestamp?: number): Promise<string> {
    try {
      const currentTime = timestamp || Date.now()
      const secretBytes = this.base32Decode(secret)
      const counterBytes = this.timestampToCounter(currentTime)

      const hash = await this.hmacSha1(secretBytes, counterBytes)
      const code = this.dynamicTruncate(hash)

      return code.toString().padStart(TOTPGenerator.DIGITS, '0')
    } catch (error) {
      throw new Error(
        `TOTP generation failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  getRemainingTime(timestamp?: number): number {
    const currentTime = timestamp || Date.now()
    const secondsElapsed = Math.floor(currentTime / 1000) % TOTPGenerator.PERIOD
    return TOTPGenerator.PERIOD - secondsElapsed
  }

  getTimeUntilNextCode(timestamp?: number): number {
    return this.getRemainingTime(timestamp)
  }

  validateSecret(secret: string): boolean {
    try {
      const cleaned = secret.toUpperCase().replace(/[^A-Z2-7]/g, '')
      if (cleaned.length === 0) return false

      this.base32Decode(cleaned)
      return true
    } catch {
      return false
    }
  }

  parseQRCodeURL(url: string): {
    issuer?: string
    account?: string
    secret: string
  } {
    try {
      const parsedUrl = new URL(url)

      if (parsedUrl.protocol !== 'otpauth:' || parsedUrl.host !== 'totp') {
        throw new Error('Invalid TOTP URL format')
      }

      const secret = parsedUrl.searchParams.get('secret')
      if (!secret) {
        throw new Error('No secret found in URL')
      }

      if (!this.validateSecret(secret)) {
        throw new Error('Invalid secret in URL')
      }

      const issuer = parsedUrl.searchParams.get('issuer') || undefined
      const pathParts = parsedUrl.pathname.split('/')
      const label = pathParts[pathParts.length - 1]

      let account: string | undefined
      if (label) {
        if (label.includes(':')) {
          account = label.split(':')[1]
        } else {
          account = label
        }
      }

      return {
        issuer,
        account,
        secret: secret.toUpperCase().replace(/[^A-Z2-7]/g, '')
      }
    } catch (error) {
      throw new Error(
        `Invalid QR code URL: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  formatSecret(secret: string): string {
    return secret.toUpperCase().replace(/[^A-Z2-7]/g, '')
  }

  static getPeriod(): number {
    return TOTPGenerator.PERIOD
  }

  static getDigits(): number {
    return TOTPGenerator.DIGITS
  }
}

export { TOTPGenerator }
